/**
 * R174: Profession-skill store. Tracks per-profession skill level
 * (1..300 WotLK 3.3.5a cap) с localStorage persist.
 *
 * Server-side validation TODO Phase 2 — пока всё клиентское,
 * чтобы не блокировать UX.
 */

import { create } from 'zustand';
import type { ProfessionId } from '../data/professions.js';
export type { ProfessionId };

export interface ProfessionSkill {
  id: ProfessionId;
  rank: number;     // 1..300
  cap: number;      // current cap (apprentice=75, journeyman=150, expert=225, artisan=300)
  learned: boolean;
}

const STORAGE_KEY = 'aiwari-prof-skills';
const RANK_CAP_TIERS = [
  { name: 'Подмастерье', cap: 75 },
  { name: 'Мастеровой', cap: 150 },
  { name: 'Эксперт', cap: 225 },
  { name: 'Знаток', cap: 300 },
];

function loadSkills(): Record<ProfessionId, ProfessionSkill> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : ({} as Record<ProfessionId, ProfessionSkill>);
  } catch {
    return {} as Record<ProfessionId, ProfessionSkill>;
  }
}
function saveSkills(skills: Record<ProfessionId, ProfessionSkill>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(skills));
  window.dispatchEvent(new CustomEvent('aiwari-prof-skills-changed', { detail: { skills } }));
}

interface ProfSkillState {
  skills: Record<ProfessionId, ProfessionSkill>;
  learn: (id: ProfessionId) => void;
  upgrade: (id: ProfessionId) => boolean; // → true если апнул tier
  gain: (id: ProfessionId, amount: number) => boolean; // → true если был skill-up
}

export const useProfessionSkill = create<ProfSkillState>((set, get) => ({
  skills: loadSkills(),
  learn: (id) => {
    const cur = get().skills[id];
    if (cur?.learned) return;
    const next = {
      ...get().skills,
      [id]: { id, rank: 1, cap: 75, learned: true },
    };
    set({ skills: next });
    saveSkills(next);
  },
  upgrade: (id) => {
    const s = get().skills[id];
    if (!s?.learned) return false;
    const tierIdx = RANK_CAP_TIERS.findIndex((t) => t.cap === s.cap);
    if (tierIdx < 0 || tierIdx >= RANK_CAP_TIERS.length - 1) return false;
    if (s.rank < s.cap - 5) return false; // нельзя апнуть пока не подкачался
    const newCap = RANK_CAP_TIERS[tierIdx + 1].cap;
    const next = { ...get().skills, [id]: { ...s, cap: newCap } };
    set({ skills: next });
    saveSkills(next);
    return true;
  },
  gain: (id, amount) => {
    const s = get().skills[id];
    if (!s?.learned) return false;
    if (s.rank >= s.cap) return false;
    const newRank = Math.min(s.cap, s.rank + amount);
    if (newRank === s.rank) return false;
    const next = { ...get().skills, [id]: { ...s, rank: newRank } };
    set({ skills: next });
    saveSkills(next);
    return true;
  },
}));

export function tierName(cap: number): string {
  const t = RANK_CAP_TIERS.find((x) => x.cap === cap);
  return t?.name ?? 'Подмастерье';
}
